// ============================================================
// Money-string utilities for the live business accounting API.
//
// Amounts arrive on the wire as decimal STRINGS ("1234.5600" — BigDecimal
// serialized at 4dp), never JSON numbers.
//
// RULE: never use parseFloat/Number on money for ARITHMETIC. These helpers
// parse digit strings into exact integer cents (display/validation only)
// and all math happens on integer cents, so no floating-point rounding can
// occur. Amounts are never round-tripped through Float back to the API —
// user-entered strings are sent verbatim.
// ============================================================

const AMOUNT_REGEX = /^(-)?(\d+)(?:\.(\d{1,4}))?$/;
const INPUT_AMOUNT_REGEX = /^(\d+)(?:\.(\d{1,2}))?$/;

/**
 * Parse a wire money string (up to 4dp, optional leading "-") into exact
 * integer cents. Sub-cent digits are rounded half-up (wire amounts are 4dp;
 * USD ledger values are 2dp-representable in practice).
 * Returns null when the string is not a valid decimal amount.
 */
export function amountToCents(amount: string): number | null {
  const match = AMOUNT_REGEX.exec(amount.trim());
  if (!match) return null;
  const [, sign, whole, frac = ''] = match;
  const frac4 = frac.padEnd(4, '0');
  // Integer-only arithmetic (digits parsed as integers — no parseFloat).
  let cents = Number(whole) * 100 + Number(frac4.slice(0, 2));
  if (Number(frac4.slice(2)) >= 50) cents += 1;
  return sign ? -cents : cents;
}

/**
 * Strict parser for USER-ENTERED amounts (journal-entry form): positive,
 * at most 2 decimal places. Returns exact integer cents, or null when the
 * input is not a valid amount.
 */
export function parseInputAmountToCents(input: string): number | null {
  const match = INPUT_AMOUNT_REGEX.exec(input.trim());
  if (!match) return null;
  const [, whole, frac = ''] = match;
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

/** True when a wire amount is negative (sign lives in the string). */
export function isNegativeAmount(amount: string): boolean {
  const cents = amountToCents(amount);
  return cents !== null && cents < 0;
}

/** True when a wire amount is exactly zero (e.g. "0.0000"). */
export function isZeroAmount(amount: string | null | undefined): boolean {
  if (amount == null) return true;
  return amountToCents(amount) === 0;
}

/**
 * Parse a wire money string into exact 4dp units (ten-thousandths), the full
 * precision the API actually sends. Unlike amountToCents this rounds NOTHING
 * away — "0.0040" is 40, not 0.
 */
export function amountToWireUnits(amount: string): number | null {
  const match = AMOUNT_REGEX.exec(amount.trim());
  if (!match) return null;
  const [, sign, whole, frac = ''] = match;
  // Integer-only arithmetic (digit strings parsed as integers).
  const units = Number(whole) * 10000 + Number(frac.padEnd(4, '0'));
  return sign ? -units : units;
}

/**
 * True only when a wire amount is zero at FULL wire precision.
 *
 * `isZeroAmount` rounds to cents first, so it calls "0.0040" zero — and a
 * sub-cent residue is exactly what the server refuses to complete a
 * reconciliation on (`difference.round(4).zero?`). Anywhere a zero test
 * gates a "you are done" affordance or hides a discrepancy, this is the
 * test to use; `isZeroAmount` is for display-level questions only.
 */
export function isZeroAtWirePrecision(amount: string | null | undefined): boolean {
  if (amount == null) return true;
  return amountToWireUnits(amount) === 0;
}

/**
 * Format a wire money string WITHOUT rounding: two decimals normally, all
 * four when there is a sub-cent residue ("$0.0040"). Use it for any figure a
 * user is being asked to drive to zero — `formatAmount` would render that
 * same residue as "$0.00" and leave the screen contradicting itself.
 */
export function formatWireAmount(amount: string, accounting = true): string {
  const units = amountToWireUnits(amount);
  if (units === null) return amount;
  const abs = Math.abs(units);
  const dollars = Math.floor(abs / 10000);
  const frac = String(abs % 10000).padStart(4, '0');
  const subCent = frac.slice(2);
  const formatted =
    `$${dollars.toLocaleString('en-US')}.${frac.slice(0, 2)}` +
    (subCent === '00' ? '' : subCent);
  if (units >= 0) return formatted;
  return accounting ? `(${formatted})` : `-${formatted}`;
}

/** Format integer cents as a 2dp USD display string. */
function centsToDisplay(absCents: number): string {
  const dollars = Math.floor(absCents / 100);
  const centsPart = String(absCents % 100).padStart(2, '0');
  return `$${dollars.toLocaleString('en-US')}.${centsPart}`;
}

/**
 * Format a wire money string for display: always 2 decimal places, with
 * accounting-style parentheses for negatives by default ("(1,234.56)").
 * Pass accounting=false for a minus sign instead. Invalid strings are
 * returned unchanged (defensive — the API contract guarantees validity).
 */
export function formatAmount(amount: string, accounting = true): string {
  const cents = amountToCents(amount);
  if (cents === null) return amount;
  const formatted = centsToDisplay(Math.abs(cents));
  if (cents >= 0) return formatted;
  return accounting ? `(${formatted})` : `-${formatted}`;
}

/**
 * Format a wire money string for an EDITABLE input: at most 2 decimals, with
 * no "$" and no thousands separators — the value is sent back to the API
 * verbatim, and a comma is not a number it will take.
 *
 * Trailing zeroes are dropped ("1450.0000" → "1450") so an editor is not
 * retyping them, and sub-cent digits are rounded away rather than shown: a
 * field labelled ($) that reads back "1233.3333" is asking the user to
 * approve a figure no invoice will ever be written for. Null and blank
 * become '' — an empty optional field, not "0".
 */
export function toAmountInput(amount: string | null | undefined): string {
  if (amount == null || !amount.trim()) return '';
  const cents = amountToCents(amount);
  if (cents === null) return amount;
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const frac = String(abs % 100).padStart(2, '0').replace(/0+$/, '');
  return `${sign}${Math.floor(abs / 100)}${frac === '' ? '' : `.${frac}`}`;
}

/**
 * The inverse of amountToWireUnits: exact ten-thousandths back to the 4dp
 * wire string ("12490000" → "1249.0000"). For a figure computed in the
 * browser and sent back for the server to compare as a decimal — an
 * estimate's selected subtotal — so it never passes through a float.
 */
export function wireUnitsToAmount(units: number): string {
  const sign = units < 0 ? '-' : '';
  const abs = Math.abs(units);
  return `${sign}${Math.floor(abs / 10000)}.${String(abs % 10000).padStart(4, '0')}`;
}
